import React, { useState } from "react"
import { API, Auth } from "aws-amplify"
import { Paper } from "@material-ui/core"
import Button from "@mui/material/Button"
import { HelveticaBold } from "common/typographies"
import i18n from "i18n"

import ModalDecimalsSettings from "./ModalDecimalsSettings"
import PropTypes from "prop-types"

const DecimalsSection = ({ organizationFields }) => {
  const [open, setOpen] = useState(false)

  const [fetching, setFetching] = useState(false)
  const [saving, setSaving] = useState(false)
  const [coefficients, setCoefficients] = useState(null)

  const onSave = async values => {
    setSaving(true)
    try {
      const apiName = "CORE"

      const session = await Auth.currentSession()
      const idToken = session.getIdToken()
      const payload = idToken.payload || {}
      const orgIdFromToken =
        payload["custom:organizationId"] ||
        payload.organizationId ||
        payload.orgId
      const orgId =
        (organizationFields && organizationFields.organizationId) ||
        orgIdFromToken

      const path = `/organizations/${orgId}/coefficients`
      const headers = {
        Authorization: `Bearer ${idToken.getJwtToken()}`,
        "Access-Control-Allow-Origin": "*",
      }

      // Normalize values: empty strings -> 0, otherwise Number(value)
      const normalize = v => {
        if (v === "" || v == null) return 0
        const n = Number(v)
        return Number.isNaN(n) ? 0 : n
      }

      const body = {
        totals: normalize(values.totals),
        margins: normalize(values.margins),
        dimensions: normalize(values.dimensions),
        measurements: normalize(values.measurements),
        sub_decompositions: normalize(values.sub_decompositions),
        total_decompositions: normalize(values.total_decompositions),
        units: normalize(values.units),
        reductions: normalize(values.reductions),
      }

      const res = await API.put(apiName, path, { headers, body })
      // update local coefficients with server response if it's an object; otherwise use body
      if (res && typeof res === "object" && !Array.isArray(res)) {
        setCoefficients(res)
      } else {
        setCoefficients(body)
      }
    } catch (error) {
      console.error("Failed to save coefficients", error)
    } finally {
      setSaving(false)
      setOpen(false)
    }
  }

  const fetchCoefficients = async () => {
    setFetching(true)
    try {
      const apiName = "CORE"

      const session = await Auth.currentSession()
      const idToken = session.getIdToken()
      const payload = idToken.payload || {}
      const orgIdFromToken =
        payload["custom:organizationId"] ||
        payload.organizationId ||
        payload.orgId
      const orgId =
        (organizationFields && organizationFields.organizationId) ||
        orgIdFromToken

      const path = `/organizations/${orgId}/coefficients`
      const headers = {
        Authorization: `Bearer ${idToken.getJwtToken()}`,
        "Access-Control-Allow-Origin": "*",
      }

      const res = await API.get(apiName, path, { headers })
      setCoefficients(res)
      return res
    } catch (error) {
      console.error("Failed to fetch coefficients", error)
      setCoefficients(null)
      return null
    } finally {
      setFetching(false)
    }
  }

  return (
    <>
      <Paper elevation={0} style={{ padding: "16px", height: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <HelveticaBold
              style={{ fontSize: "18px", color: "#414141", marginBottom: 8 }}
            >
              {i18n.t("decimalsSettings.decimals")}
            </HelveticaBold>
            <div style={{ color: "#6d6d6d", fontSize: "14px", marginTop: 4 }}>
              {i18n.t("decimalsSettings.setDecimalsInfo")}
            </div>
          </div>
          <Button
            variant="contained"
            color="branded"
            style={{ marginLeft: 24, minWidth: 120 }}
            disabled={fetching}
            onClick={async () => {
              // fetch first, then open modal with coefficients ready
              const res = await fetchCoefficients()
              // only open modal after fetch completes
              setOpen(true)
            }}
          >
            {i18n.t("decimalsSettings.configure")}
          </Button>
        </div>
      </Paper>
      <ModalDecimalsSettings
        open={open}
        onClose={() => setOpen(false)}
        onSave={onSave}
        loading={saving}
        coefficients={coefficients}
      />
    </>
  )
}

export default DecimalsSection

DecimalsSection.propTypes = {
  organizationFields: PropTypes.object,
}
